import {
    CarLocation,
    Car,
    AccessoriesResponse,
    AddAccessoryParam,
    UpdateAccessoryParams,
    CarRateClass,
    UpdateCarResponse,
    UpdateCarParams,
} from '../../types/carTypes';
import {apiSlice} from './apiSlice';
import dayjs from 'dayjs';
import {formatTimestamp, ucFirst} from '../../helpers/misc';
import {PaginatedQueryParams} from '../../types/apiTypes';

const bookCarModelName = "App\\Models\\Bookables\\BookCar";

export const carInjectedEndpoints = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getCarLocations: builder.query<CarLocation, PaginatedQueryParams>({
            query: ({page, perPage}) => {
                let startedAt = formatTimestamp(dayjs());
                let endedAt = formatTimestamp(dayjs().add(1, 'seconds'));

                startedAt = encodeURIComponent(startedAt);
                endedAt = encodeURIComponent(endedAt);
                return `/cars/cars_and_locations?page=${page}&per_page=${perPage}&started_at=${startedAt}&ended_at=${endedAt}&sort=number`;
            },
        }),
        getCar: builder.query<Car, number>({
            query: (id) => `/cars/${id}`,
            providesTags: (result, error, id) => result ? [{type: "Car", id}, "Car"] : ["Car"],
        }),
        getAccessories: builder.query<AccessoriesResponse, PaginatedQueryParams>({
            query: ({page, perPage}) => `/accessories?page=${page}&per_page=${perPage}`,
            providesTags: ["Accessory"],
        }),
        getCarRateClasses: builder.query<string[], void>({
            query: () => {
                const model = encodeURIComponent(bookCarModelName);
                const option = encodeURIComponent('rate_class');

                return `/options/model?model=${model}&option=${option}`;
            },
            transformResponse: (response: CarRateClass[]) => {
                return response.map((res) => ucFirst(res.name));
            },
        }),
        addAccessory: builder.mutation<{ id: number }, AddAccessoryParam>({
            query: ({name}) => ({
                url: `/accessories`,
                method: 'POST',
                body: {name},
            }),
            invalidatesTags: ["Accessory"],
        }),
        updateAccessory: builder.mutation<[], UpdateAccessoryParams>({
            query: ({id, name}) => ({
                url: `/accessories/${id}`,
                method: 'PUT',
                body: {name},
            }),
            invalidatesTags: ["Accessory"],
        }),
        deleteAccessory: builder.mutation<[], number>({
            query: (id) => ({
                url: `/accessories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ["Accessory"],
        }),
        updateCar: builder.mutation<UpdateCarResponse, UpdateCarParams>({
            query: ({carID, requestData}) =>({
                url: `/cars/${carID}`,
                method: 'PUT',
                body: requestData,
            }),
            invalidatesTags: (result, error, {carID}) => [{type: "Car", carID}],
        }),
    }),
});

export const carEndpoints = carInjectedEndpoints.endpoints;
export const {
    useGetCarLocationsQuery,
    useGetCarQuery,
    useGetAccessoriesQuery,
    useAddAccessoryMutation,
    useUpdateAccessoryMutation,
    useDeleteAccessoryMutation,
    useGetCarRateClassesQuery,
    useUpdateCarMutation,
} = carInjectedEndpoints;
